import {
  AfterViewInit,
  Component,
  computed,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { v4 as uuid } from 'uuid';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  Subscription,
  take,
  withLatestFrom,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { fareFamiliesWithoutCampaignFiltering } from '@fcom/common/config/booking-config';
import { Cabin, TranslatedBenefitRow } from '@fcom/dapi/api/models';
import { Amount } from '@fcom/dapi/interfaces';
import { isDeepEqual, isPresent, unsubscribe } from '@fcom/core/index';
import { NotificationButtonStyle, TagTheme } from '@fcom/ui-components';
import { CabinClassSelectionFareFamily } from '@fcom/booking/modules/ticket-selection/interfaces';
import { effectiveCabinClass } from '@fcom/booking/modules/ticket-selection/utils/offer.utils';
import { ScrollService } from '@fcom/common/services';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { FinnairThemeService } from '@fcom/common';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { finShare } from '@fcom/rx';

import { getBrandNameShortCode, getCabinClassShortCode } from '../../../utils/common-booking.utils';
import { DimensionsAndDetails, ExtendedFareFamily } from '../../../interfaces';

const SEATS_LEFT_LIMIT = 9;
type FareFamilyByCabin = Record<Cabin, ExtendedFareFamily[]>;
type MinPricePerCabin = Record<
  Cabin,
  {
    totalPrice: Amount;
    price: Amount;
    originalTotalPrice?: Amount;
    originalPrice?: Amount;
    quota: number;
    discountReasonCode?: string;
    isSpecialOffer?: boolean;
    points?: string;
    disabled?: boolean;
  }
>;

@Component({
  selector: 'fin-fare-family-list',
  styleUrls: ['./fare-family-list.component.scss'],
  templateUrl: './fare-family-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlightSelectionFareFamilyListComponent implements OnInit, OnDestroy, AfterViewInit {
  private scrollService = inject(ScrollService);
  private gtmService = inject(GtmService);
  private document = inject<Document>(DOCUMENT);
  private themeService = inject(FinnairThemeService);

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  readonly Cabin = Cabin;
  readonly NotificationButtonStyle = NotificationButtonStyle;
  readonly SEATS_LEFT_LIMIT = SEATS_LEFT_LIMIT;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TagTheme = TagTheme;

  @Input({ required: true }) fareFamilies$: Observable<ExtendedFareFamily[]>;
  @Input({ required: true }) currencyCode: string;
  @Input() showUpsellCard = false;
  @Input() showKoreaNotification = false;
  @Input() roundedTop = false;
  @Input() allowAutoExpand = false;
  @Input()
  preSelectedFareFamilyId: string;
  @Input()
  isShortHaul$: Observable<boolean>;
  @Input()
  isLongHaulLegAndAy$: Observable<boolean> = EMPTY;
  @Input()
  isShowTotalPrice = true;
  @Input()
  enableFareFamilyAutoScroll$: Observable<boolean> = of(false);
  @Input()
  isAwardFlow = false;
  @Input()
  isMixedCabin = false;

  @Output() selectFareFamily = new EventEmitter<{
    fareFamily: ExtendedFareFamily;
    checked: boolean;
    index?: number;
  }>();

  @Output() cabinIsExpanded = new EventEmitter<Cabin>();

  @Output() openCabinClassModal = new EventEmitter<string>();

  @Output() fareFamilyAutoScrolled = new EventEmitter<void>();

  private subscriptions: Subscription = new Subscription();

  fareFamiliesByCabin$: Observable<FareFamilyByCabin>;
  cabins$: Observable<Cabin[]>;
  cabinIsExpanded$ = new BehaviorSubject<Cabin>(null);
  minPricePerCabin$: Observable<MinPricePerCabin>;
  selectedFareFamilyPerCabin$: Observable<Record<Cabin, ExtendedFareFamily>>;
  fareFamilyBenefits$: Observable<Record<string, TranslatedBenefitRow[]>>;
  uuid: string;
  isTicketTypeModalOpen = false;
  ticketTypeInfo$: Observable<DimensionsAndDetails>;
  cabinUpsellMap = {
    [Cabin.ECONOMY]: Cabin.ECOPREMIUM,
    [Cabin.ECOPREMIUM]: Cabin.BUSINESS,
    [Cabin.BUSINESS]: Cabin.FIRST,
  };
  defaultCabinOrder = [Cabin.ECONOMY, Cabin.ECOPREMIUM, Cabin.BUSINESS, Cabin.FIRST];
  upsellCardFareFamily$: Observable<CabinClassSelectionFareFamily>;
  shouldShowUpsellCard$: Observable<boolean> = of(false);

  // These are used for autoscroll for preselected fare family
  carouselReady$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  cabinExpandedAnimationEnded$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  ngOnInit(): void {
    this.uuid = uuid();
    this.cabins$ = this.isMixedCabin
      ? of([Cabin.MIXED])
      : this.fareFamilies$.pipe(
          filter(Boolean),
          map((fareFamilies) => {
            return fareFamilies.reduce((acc: Cabin[], fareFamily) => {
              const cabin = effectiveCabinClass(fareFamily);
              if (cabin && !acc.includes(cabin)) {
                acc.push(cabin);
              }
              return acc;
            }, []);
          }),
          map((cabinList) =>
            cabinList.sort((a, b) => this.defaultCabinOrder.indexOf(a) - this.defaultCabinOrder.indexOf(b))
          ),
          finShare()
        );
    this.fareFamiliesByCabin$ = this.fareFamilies$.pipe(
      filter(Boolean),
      withLatestFrom(this.cabins$),
      map(([fareFamilies, cabins]) => {
        if (this.isAwardFlow) {
          return Object.fromEntries(cabins.map((cabin) => [cabin, fareFamilies])) as Record<
            Cabin,
            ExtendedFareFamily[]
          >;
        }
        if (this.isMixedCabin) {
          return {
            [Cabin.MIXED]: fareFamilies,
          } as Record<Cabin, ExtendedFareFamily[]>;
        }
        return fareFamilies.reduce(
          (acc, fareFamily) => {
            const cabin = effectiveCabinClass(fareFamily);
            if (!cabin) {
              return acc;
            }

            acc[cabin] = acc[cabin] || [];
            if (
              this.isNormalFareFamily(fareFamily) &&
              (this.hasMatchingCampaignFareFamily(fareFamily, fareFamilies, cabin) ||
                this.hasMatchingSuperLightCampaign(fareFamily, fareFamilies))
            ) {
              return acc;
            }

            acc[cabin].push(fareFamily);
            return acc;
          },
          {} as Record<Cabin, ExtendedFareFamily[]>
        );
      }),
      finShare()
    );

    this.selectedFareFamilyPerCabin$ = this.fareFamilies$.pipe(
      filter(Boolean),
      distinctUntilChanged((a, b) => isDeepEqual(a, b)),
      map((fareFamilies) => {
        return fareFamilies.reduce(
          (acc, fareFamily) => {
            const cabinClass = this.isMixedCabin ? Cabin.MIXED : effectiveCabinClass(fareFamily);
            if (!cabinClass) {
              return acc;
            }

            acc[cabinClass] = acc[cabinClass] || null;

            if (fareFamily.selected) {
              acc[cabinClass] = fareFamily;
            }

            return acc;
          },
          {} as Record<Cabin, ExtendedFareFamily>
        );
      }),
      finShare()
    );

    this.subscriptions.add(
      this.cabins$.pipe(take(1)).subscribe((cabins) => {
        if (cabins.length === 1 && this.allowAutoExpand) {
          this.cabinIsExpanded$.next(cabins[0]);
        }
      })
    );

    this.minPricePerCabin$ = this.fareFamilies$.pipe(
      filter(Boolean),
      map((fareFamilies) => {
        return fareFamilies.reduce((acc, fareFamily) => {
          const cabin = this.isMixedCabin ? Cabin.MIXED : effectiveCabinClass(fareFamily);
          if (!cabin) {
            return acc;
          }

          const currentLowestPrice = acc[cabin]?.totalPrice;

          if (!currentLowestPrice || Number(fareFamily.totalPrice) < Number(currentLowestPrice.amount)) {
            acc[cabin] = {
              totalPrice: {
                amount: fareFamily.totalPrice,
                currencyCode: this.currencyCode,
              },
              price: {
                amount: fareFamily.price,
                currencyCode: this.currencyCode,
              },
              points: fareFamily?.points,
              disabled: this.isAwardFlow ? fareFamily.disabled : false,
              isSpecialOffer: fareFamily?.isSpecialOffer,
              discountReasonCode: fareFamily?.discountReasonCode,
              quota: fareFamily.quota || 9,
              ...(fareFamily.originalPrice
                ? {
                    originalPrice: {
                      amount: fareFamily.originalPrice,
                      currencyCode: this.currencyCode,
                    },
                  }
                : {}),
              ...(fareFamily.originalTotalPrice
                ? {
                    originalTotalPrice: {
                      amount: fareFamily.originalTotalPrice,
                      currencyCode: this.currencyCode,
                    },
                  }
                : {}),
            };
          }

          return acc;
        }, {} as MinPricePerCabin);
      }),
      finShare()
    );

    this.ticketTypeInfo$ = combineLatest([this.fareFamilies$, this.cabinIsExpanded$]).pipe(
      map(([fareFamilies, expandedCabin]) => {
        if (!expandedCabin) {
          return null;
        }

        return {
          extendedFareFamilies: fareFamilies,
          cabinClass: this.isMixedCabin ? effectiveCabinClass(fareFamilies[0]) : expandedCabin,
          itineraries: [],
        };
      }),
      finShare()
    );

    this.upsellCardFareFamily$ = combineLatest([this.cabinIsExpanded$, this.fareFamiliesByCabin$]).pipe(
      map(([currentCabin, fareFamiliesByCabin]) => {
        if (!currentCabin) {
          return null;
        }
        const nextCabinClass = this.findNextAvailableCabin(currentCabin, fareFamiliesByCabin);

        const nextCabinFareFamily: ExtendedFareFamily[] = fareFamiliesByCabin[nextCabinClass];

        if (!nextCabinFareFamily) {
          return null;
        }
        return {
          ...nextCabinFareFamily[0],
          cabinClass: nextCabinClass,
          preSelectFareFamily: nextCabinFareFamily[0],
        } as CabinClassSelectionFareFamily;
      }),
      finShare()
    );

    this.shouldShowUpsellCard$ = combineLatest([this.upsellCardFareFamily$, this.isShortHaul$]).pipe(
      map(([upsellCardFareFamily, isShortHaul]) => {
        if (!this.showUpsellCard) {
          return false;
        }

        if (!isPresent(upsellCardFareFamily) && !isShortHaul) {
          return true;
        }

        return !!upsellCardFareFamily;
      }),
      finShare()
    );

    this.subscriptions.add(
      this.cabinIsExpanded$.pipe(distinctUntilChanged(), filter(Boolean)).subscribe((cabin) => {
        this.cabinIsExpanded.emit(cabin);
      })
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.enableFareFamilyAutoScroll$,
        this.carouselReady$,
        this.cabinExpandedAnimationEnded$,
        this.cabinIsExpanded$,
      ])
        .pipe(
          distinctUntilChanged(),
          debounceTime(100),
          filter(
            ([isFareFamilyAutoScrollEnabled, isReady, isEnded, isCabinExpanded]) =>
              isFareFamilyAutoScrollEnabled && isReady && isEnded && Boolean(isCabinExpanded)
          ),
          finShare()
        )
        .subscribe(() => {
          const element = this.document.getElementById(this.preSelectedFareFamilyId);
          if (element) {
            this.scrollService.scrollToImmediate(element, 100, true, true);
            this.fareFamilyAutoScrolled.emit();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  toggleCabin(cabin: Cabin): void {
    const nextValue = this.cabinIsExpanded$.getValue() === cabin ? null : cabin;
    this.cabinIsExpanded$.next(nextValue);
  }

  trackByFn(index: number, item: ExtendedFareFamily): string {
    return `${item.id}-${index}`;
  }

  trackByCabin(index: number, cabin: Cabin): string {
    return `${cabin}-${index}`;
  }

  openCabinAndSelect(fareFamily: CabinClassSelectionFareFamily): void {
    this.cabinIsExpanded$.next(fareFamily.cabinClass as Cabin);
  }

  afterCarouselViewInit(): void {
    if (this.preSelectedFareFamilyId) {
      this.carouselReady$.next(true);
    }
  }

  cabinExpanded(): void {
    if (this.preSelectedFareFamilyId) {
      this.cabinExpandedAnimationEnded$.next(true);
    }
  }

  openBenefitModal(): void {
    this.isTicketTypeModalOpen = true;
    this.gtmService.trackElement(
      'benefits',
      GaContext.FLIGHT_SELECTION,
      ElementTypes.MODAL,
      undefined,
      ElementActions.OPEN
    );
  }

  private findNextAvailableCabin = (currentCabin: Cabin, fareFamiliesByCabin: FareFamilyByCabin) => {
    let nextCabin = this.cabinUpsellMap[currentCabin];
    // Loop to find the next available cabin by checking the keys in fareFamiliesByCabin
    while (nextCabin) {
      if (Object.keys(fareFamiliesByCabin).some((cabin) => cabin === nextCabin)) {
        return nextCabin;
      }
      nextCabin = this.cabinUpsellMap[nextCabin];
    }
    return null;
  };

  private isNormalFareFamily(fareFamily: ExtendedFareFamily): boolean {
    return !fareFamily.fareFamilyCode.includes('CAMP');
  }

  private hasMatchingSuperLightCampaign(
    targetFareFamily: ExtendedFareFamily,
    fareFamilies: ExtendedFareFamily[]
  ): boolean {
    const targetBrandNameLower = targetFareFamily.benefits?.brandName?.toLowerCase();
    const hasSuperLightCampaign = fareFamilies.some((fareFamily) => {
      const fareFamilyBrandNameLower = fareFamily.benefits?.brandName?.toLowerCase();

      return (
        fareFamilyBrandNameLower &&
        fareFamilyBrandNameLower.includes('superlight') &&
        fareFamilyBrandNameLower.includes('campaign')
      );
    });

    return (
      hasSuperLightCampaign &&
      targetBrandNameLower &&
      targetBrandNameLower.includes('superlight') &&
      !targetBrandNameLower.includes('campaign')
    );
  }

  private hasMatchingCampaignFareFamily(
    targetFareFamily: ExtendedFareFamily,
    fareFamilies: ExtendedFareFamily[],
    cabinClass: string
  ): boolean {
    if (
      fareFamilies.some(
        (ff) => ff.fareFamilyCode === fareFamiliesWithoutCampaignFiltering[targetFareFamily.benefits?.fareFamilyCode]
      )
    ) {
      return false;
    }
    const codeStart = getCabinClassShortCode(cabinClass);
    const brandCode = getBrandNameShortCode(targetFareFamily.benefits?.brandName);
    const campaignCode = new RegExp(`^${codeStart}${brandCode}CAMP`);
    return fareFamilies.some((ff) => campaignCode.test(ff.fareFamilyCode));
  }
}
